/**
 * Merge a caller's `className` onto a component's own base class.
 *
 * ── WHY THIS EXISTS ──
 *
 * The house idiom for a pass-through part is
 *
 *   export function CardHeader(props: React.ComponentProps<"div">) {
 *     return <div className="FfnCardHeader" {...props} />
 *   }
 *
 * and it has a quiet defect: `props` spreads AFTER the literal, so a caller that
 * passes `className` DELETES the base class. The part then renders as a bare
 * element — no border, no surface, no radius, no padding — while TypeScript stays
 * happy and every content assertion still passes. Only the pixels are wrong.
 *
 * This was fixed once in Button (2026-08-14, found visually in AuthForm's SSO
 * row) and once in Combobox, both as one-off local fixes. Wave 9 hit it a THIRD
 * time: PricingTable passed `className="FfnPricingCard"` to Card, and the
 * marketing--06 crop showed three borderless cards. A DOM probe reported
 * `borderWidth: 0px` on an element whose stylesheet says 1px.
 *
 * Three occurrences of one defect class is a missing abstraction, not three
 * mistakes — so the merge lives here, every pass-through part calls it, and
 * `tests/class-merge.test.tsx` enumerates the whole surface and fails when a new
 * part forgets.
 *
 * ── WHAT ABOUT PARTS THAT REFUSE className? ──
 *
 * The typography roles deliberately `Omit<…, "className">`: there the role IS the
 * styling contract, and letting a call site re-skin one instance would drift the
 * role. Those are correct as they are and are exempt by construction — they
 * cannot receive a className to drop.
 */
export function mergeClassName(base: string, incoming: string | undefined): string {
  return incoming === undefined || incoming === "" ? base : `${base} ${incoming}`
}

/**
 * The Base UI variant. Its parts type `className` as
 * `string | ((state) => string | undefined)`, because a part can style itself
 * from its own state — and a function form cannot be string-concatenated.
 *
 * So the merge is deferred: given a function, return a FUNCTION that calls the
 * caller's with the state and merges the result. Flattening a function form to a
 * string here would silently discard the state-dependent half, which is worse
 * than the defect this file exists to fix.
 */
export function mergeBaseUiClassName<State>(
  base: string,
  incoming: string | ((state: State) => string | undefined) | undefined
): string | ((state: State) => string) {
  if (typeof incoming === "function") {
    return (state: State) => mergeClassName(base, incoming(state))
  }
  return mergeClassName(base, incoming)
}
